import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, ConnectableObservable } from "rxjs";
import { publishLast, takeUntil, tap } from 'rxjs/operators';
import { ISecureStorage } from "../../classes/interfaces/isecurestorage";

import { withSpinner } from "../../extensions/withSpinner.extension";
import { AccountCategory, Community, Contact, Lot, LotContact, LotDetail, PhaseRelease } from "../../types";
import { API_ROOT, SECURE_LOCAL_STORAGE } from "../../types/constants/appconstants";
import { OrgSelectionService } from "../orgselection.service";

@Injectable({
    "providedIn": "root"
})
export class LotsService {
    private http = inject(HttpClient);
    private apiRoot = inject(API_ROOT);
    private orgSelectionService = inject(OrgSelectionService);
    private storage = inject<ISecureStorage>(SECURE_LOCAL_STORAGE);
    private lotsBaseUrl = this.apiRoot + "api/lots";

    private lastLotDetailsLotId: number = null;
    private lotDetails: ConnectableObservable<LotDetail>;
    private lotFieldContactsStorageKey: string = "LotFieldContacts";

    getCommunityLots(commId: number): Observable<Lot[]> {
        return withSpinner(this.http).get<Lot[]>(`${this.lotsBaseUrl}/communitylots/${commId}`);
    }

    getFilteredCommunityLots(communityIds: number[], filterType: number): Observable<Lot[]> {
        return withSpinner(this.http).post<Lot[]>(`${this.lotsBaseUrl}/communitylots`, { communityIds, filterType });
    }

    getCommunityLotFieldContacts(orgId: number): Observable<any[]> {
        // GET /communitylotfieldcontacts/{orgId}/lots
        return withSpinner(this.http).get<any[]>(`${this.lotsBaseUrl}/communitylotfieldcontacts/${orgId}/lots`);
    }

    getLotDetails(lotId: number, forceReload: boolean = false): Observable<LotDetail> {
        if (this.lastLotDetailsLotId === null || this.lastLotDetailsLotId !== lotId || forceReload) {
            this.lastLotDetailsLotId = lotId;

            this.lotDetails = withSpinner(this.http).get<LotDetail>(`${this.lotsBaseUrl}/lotdetails/${lotId}`).pipe(
                publishLast()
            ) as ConnectableObservable<LotDetail>;
            this.lotDetails.connect();
        }
        return this.lotDetails;
    }

    clearLotDetailsId(): void {
        this.lastLotDetailsLotId = null;
    }

    getAssignedFieldContactsForLotDetail(lotId: number): Observable<Contact[]> {
        // GET lotdetail/contacts/{lotId}
        return withSpinner(this.http).get<Contact[]>(`${this.lotsBaseUrl}/lotdetail/contacts/${lotId}`);
    }

    getLotsByUser(marketId: number, azureGUID: string): Observable<Lot[]> {
        // GET /{marketId}/lots/contacts/{azureGUID}
        return withSpinner(this.http).get<Lot[]>(`${this.lotsBaseUrl}/${marketId}/lots/contacts/${azureGUID}`);
    }

    getLotsForPurchaseOrders(communityIds: number[], includeContacts: boolean = false, onlyThoseWithPurchaseOrders: boolean = false, includeDeletedLots: boolean = false): Observable<Lot[]> {
        return withSpinner(this.http, "Loading Lots...").post<Lot[]>(this.lotsBaseUrl, { communityIds, includeContacts, onlyThoseWithPurchaseOrders, includeDeletedLots });
    }

    getLotsForScheduleAccountCategories(comms: Community[], acctCats: AccountCategory[]): Observable<Lot[]> {
        // POST /communityschedulelots
        return withSpinner(this.http).post<Lot[]>(`${this.lotsBaseUrl}/communityschedulelots`, { comms: comms, acctCategories: acctCats });
    }

    getLotsForScheduleMassTaskAdd(comms: Community[], sourceMasterTaskId: number, addMasterTaskId: number): Observable<Lot[]> {
        return withSpinner(this.http).get<Lot[]>(`${this.lotsBaseUrl}/schedulemassaddlots/${addMasterTaskId}/${sourceMasterTaskId}?commIds=[${comms.map(c => c.orgId).join(",")}]`);
    }

    getLotsForScheduleMassTaskDelete(comms: Community[], deleteMasterTaskId: number): Observable<Lot[]> {
        return withSpinner(this.http).get<Lot[]>(`${this.lotsBaseUrl}/schedulemassdeletelots/${deleteMasterTaskId}?commIds=[${comms.map(c => c.orgId).join(",")}]`);
    }

    getMasterUnitLotsWithoutJobForCommunity(commId: number): Observable<Lot[]> {
        // GET /masterunitlots/{commId}
        return withSpinner(this.http).get<Lot[]>(`${this.lotsBaseUrl}/masterunitlots/${commId}`);
    }

    getPhaseReleaseOptions(comms: Community[]): Observable<PhaseRelease[]> {
        // POST /phaseReleaseOptions
        return withSpinner(this.http).post<PhaseRelease[]>(`${this.lotsBaseUrl}/phaseReleaseOptions`, { comms: comms });
    }

    saveLotContacts(lots: any[], contact: any, marketId: number): Observable<any> {
        // POST /savelotcontacts
        return withSpinner(this.http).post(`${this.lotsBaseUrl}/savelotcontacts`, { user: contact, lots: lots, marketId: marketId }).pipe(
            tap(c => this.clearLotContactsCache())
        );
    }

    saveMultipleContactsToLot(lotId: number, lotContacts: LotContact[], marketId: number): Observable<any> {
        // POST /savecontactstolot
        return withSpinner(this.http).put<any>(`${this.lotsBaseUrl}/savecontactstolot`, { lotId: lotId, lotContacts: lotContacts, marketId: marketId }).pipe(
            tap(c => this.clearLotContactsCache())
        );
    }

    updateLot(lot: any, lotContacts: LotContact[]): Observable<any> {
        return withSpinner(this.http).put<any>(`${this.lotsBaseUrl}`, { "lot": lot, "lotContacts": lotContacts }).pipe(
            tap(c => this.clearLotContactsCache())
        );
    }

    clearLotContactsCache() {
        this.orgSelectionService.selectedMarket
            .pipe(tap(org => {
                this.storage.removeItem(`${this.lotFieldContactsStorageKey}_${org.orgId}`);
            }));
    }
}
